import type { AxiosInstance } from 'axios';
import { create } from 'zustand';
import { http as defaultHttp } from '../runtime/http';
import { pushNotification } from '../store/notificationStore';

export type LicenseValidationState = 'valid' | 'invalid' | 'unknown';

export interface DeviceIdResponse {
  did: string;
}

export interface LicenseStatusResponse {
  enabled: boolean;
  license_path?: string | null;
}

export interface LicenseValidateResponse {
  state: LicenseValidationState;
  validate: boolean;
  message?: string | null;
  expires_at?: string | null;
  remaining_days?: number | null;
  remaining_hours?: number | null;
  details?: Record<string, unknown> | null;
}

export interface LicenseUploadResponse {
  accepted: boolean;
  message: string;
  validate?: LicenseValidateResponse | null;
  reconciled: boolean;
  reconciled_apps: string[];
}

export interface LicenseErrors {
  deviceId?: string;
  status?: string;
  validate?: string;
  upload?: string;
}

export interface LicenseState {
  loading: boolean;
  uploading: boolean;
  uploadProgress: number | null;
  deviceId: string | null;
  status: LicenseStatusResponse | null;
  validation: LicenseValidateResponse | null;
  uploadResult: LicenseUploadResponse | null;
  errors: LicenseErrors;
  lastFetchedAt: string | null;
  loadAll: () => Promise<void>;
  refresh: () => Promise<void>;
  upload: (file: File) => Promise<LicenseUploadResponse>;
}

const CHANNEL_NAME = 'blade-os:license';
const instanceId = Math.random().toString(36).slice(2);

type LicenseBusMessage = {
  type: 'license-updated';
  origin: string;
};

type HttpFactory = () => AxiosInstance;
let httpFactory: HttpFactory = defaultHttp;
let channel: BroadcastChannel | null = null;
let listening = false;
let loadAllInFlight: Promise<void> | null = null;

function getHttp(): AxiosInstance {
  return httpFactory();
}

function errorMessage(err: unknown): string {
  if (typeof err === 'object' && err !== null && 'response' in err) {
    const response = (err as { response?: { data?: { detail?: unknown } } }).response;
    const detail = response?.data?.detail;
    if (typeof detail === 'string') return detail;
  }
  if (err instanceof Error && err.message) return err.message;
  return '请求失败';
}

function getChannel(): BroadcastChannel | null {
  if (typeof BroadcastChannel === 'undefined') return null;
  if (!channel) channel = new BroadcastChannel(CHANNEL_NAME);
  return channel;
}

function ensureBusListener(): void {
  if (listening) return;
  const bus = getChannel();
  if (!bus) return;
  bus.addEventListener('message', (event: MessageEvent<LicenseBusMessage>) => {
    const msg = event.data;
    if (!msg || msg.type !== 'license-updated' || msg.origin === instanceId) return;
    void useLicenseStore.getState().loadAll();
  });
  listening = true;
}

async function capture<T>(fn: () => Promise<T>): Promise<{ data?: T; error?: string }> {
  try {
    return { data: await fn() };
  } catch (err) {
    return { error: errorMessage(err) };
  }
}

export const useLicenseStore = create<LicenseState>((set, get) => ({
  loading: false,
  uploading: false,
  uploadProgress: null,
  deviceId: null,
  status: null,
  validation: null,
  uploadResult: null,
  errors: {},
  lastFetchedAt: null,

  async loadAll() {
    if (loadAllInFlight) return loadAllInFlight;

    loadAllInFlight = (async () => {
      ensureBusListener();
      set({ loading: true });
      const client = getHttp();
      const [device, status, validate] = await Promise.all([
        capture(async () => (await client.get<DeviceIdResponse>('/licenses/device-id')).data),
        capture(async () => (await client.get<LicenseStatusResponse>('/licenses/status')).data),
        capture(async () => (await client.get<LicenseValidateResponse>('/licenses/validate')).data),
      ]);

      set((state) => ({
        loading: false,
        deviceId: device.data?.did ?? state.deviceId,
        status: status.data ?? state.status,
        validation: validate.data ?? state.validation,
        errors: {
          ...state.errors,
          deviceId: device.error,
          status: status.error,
          validate: validate.error,
        },
        lastFetchedAt: new Date().toISOString(),
      }));
    })().finally(() => {
      loadAllInFlight = null;
    });

    return loadAllInFlight;
  },

  async refresh() {
    await get().loadAll();
  },

  async upload(file: File) {
    set({ uploading: true, uploadProgress: 0, errors: { ...get().errors, upload: undefined } });
    try {
      const body = new FormData();
      body.append('file', file);
      const { data } = await getHttp().post<LicenseUploadResponse>('/licenses/upload', body, {
        onUploadProgress: (event) => {
          if (event.total) {
            set({ uploadProgress: Math.round((event.loaded / event.total) * 100) });
          }
        },
      });
      set({ uploading: false, uploadProgress: null, uploadResult: data });
      getChannel()?.postMessage({ type: 'license-updated', origin: instanceId } satisfies LicenseBusMessage);
      await get().loadAll();
      pushNotification({ message: data.message || '证书已上传', level: 'success' });
      return data;
    } catch (err) {
      const message = errorMessage(err);
      set((state) => ({
        uploading: false,
        uploadProgress: null,
        errors: { ...state.errors, upload: message },
      }));
      throw err;
    }
  },
}));

export function isLicenseEnforcedInvalid(): boolean {
  const state = useLicenseStore.getState();
  return state.status?.enabled === true && state.validation?.state === 'invalid';
}

export function installLicenseHttpForTest(factory: HttpFactory): void {
  httpFactory = factory;
}

export function resetLicenseStoreForTest(): void {
  httpFactory = defaultHttp;
  channel?.close();
  channel = null;
  listening = false;
  loadAllInFlight = null;
  useLicenseStore.setState({
    loading: false,
    uploading: false,
    uploadProgress: null,
    deviceId: null,
    status: null,
    validation: null,
    uploadResult: null,
    errors: {},
    lastFetchedAt: null,
  });
}
